import { Box, Container, Grid, Typography } from '@mui/material';
import { FC } from 'react';
import property from '../../assets/images/property.png';
import stats from '../../assets/images/stats.png';
import { CheckCircleOutline } from '@mui/icons-material';
import { useInView } from '../../hooks/useInView';

const PRIMARY = '#8c7c0c';

const FEATURES = [
    { title: 'Verified Tenants', desc: 'Attract reliable tenants with verified rental histories and ratings.' },
    { title: 'Timely Rent Payments', desc: 'Tenants can access rent credit, reducing late payments for you.' },
    { title: 'Simple Record-Keeping', desc: 'Track rent, expenses, and tenant info without spreadsheets.' },
    { title: 'Streamlined Communication', desc: 'Message tenants directly through one centralized platform.' },
    { title: 'Maintenance Tracking', desc: 'Stay on top of repair requests with status updates in real time.' },
    { title: 'Free Property Listing', desc: 'Showcase your properties at no cost — unlike traditional realtors.' },
];

const Landlord: FC = () => {
    const { ref, visible } = useInView();

    return (
        <Box component="section" id="landlord" ref={ref} sx={{ py: { xs: 8, md: 12 }, bgcolor: '#f9f8f4' }}>
            <Container maxWidth="lg">
                <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
                    {/* Copy */}
                    <Grid item xs={12} md={6}>
                        <Box className={`sg-reveal-left ${visible ? 'sg-visible' : ''}`}>
                            <Typography sx={{ display: 'inline-block', bgcolor: `${PRIMARY}12`, color: PRIMARY, px: 2, py: 0.5, borderRadius: 20, fontSize: 13, fontWeight: 600, mb: 2 }}>
                                For Landlords & Agents
                            </Typography>
                            <Typography component="h2" sx={{ fontSize: { xs: 30, md: 42 }, fontWeight: 800, color: '#111827', lineHeight: 1.2, mb: 2 }}>
                                Stop Struggling to Manage Reliable Tenants
                            </Typography>
                            <Typography sx={{ fontSize: 16, color: '#6b7280', lineHeight: 1.7, mb: 5 }}>
                                Sheltergram gives landlords and property agents the complete toolkit to find, onboard, and retain great tenants while automating the financial side.
                            </Typography>

                            <Grid container spacing={2}>
                                {FEATURES.map((f, i) => (
                                    <Grid item xs={12} sm={6} key={f.title}>
                                        <Box
                                            className={`sg-reveal ${visible ? 'sg-visible' : ''}`}
                                            style={{ transitionDelay: `${i * 0.06}s` }}
                                            sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', p: 2, borderRadius: 2, bgcolor: '#fff', border: '1px solid #e5e7eb' }}
                                        >
                                            <CheckCircleOutline sx={{ color: PRIMARY, fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                                            <Box>
                                                <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>{f.title}</Typography>
                                                <Typography sx={{ fontSize: 13, color: '#6b7280', mt: 0.3, lineHeight: 1.5 }}>{f.desc}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>

                            <Box sx={{ mt: 4 }}>
                                <a href="/auth?signup=true" className="sg-btn-primary">List Your Property Free</a>
                            </Box>
                        </Box>
                    </Grid>

                    {/* Stacked phone images */}
                    <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                        <Box className={`sg-reveal ${visible ? 'sg-visible' : ''}`} style={{ transitionDelay: '0.1s' }}>
                            <img
                                src={property}
                                alt="Property management"
                                loading="lazy"
                                style={{ height: 460, width: 'auto', objectFit: 'contain', borderRadius: 24, transform: 'rotate(-4deg)', boxShadow: '12px 12px 40px rgba(0,0,0,.15)' }}
                            />
                        </Box>
                        <Box className={`sg-reveal ${visible ? 'sg-visible' : ''}`} style={{ transitionDelay: '0.2s', marginTop: 40 }}>
                            <img
                                src={stats}
                                alt="Property stats"
                                loading="lazy"
                                style={{ height: 460, width: 'auto', objectFit: 'contain', borderRadius: 24, transform: 'rotate(4deg)', boxShadow: '12px 12px 40px rgba(0,0,0,.15)' }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default Landlord;
